<template>
  <permission-gate
    moduleKey="especificacion-tecnica"
    :showNoEditAlert="true"
  >
    <template #default>
      <main-breadcrumb
        :items="items"
        showBackButton
      ></main-breadcrumb>
      <main-buttons-bar :align="'space-between'">
        <template #left>
          <custom-title>Edición de especificación técnica</custom-title>
        </template>
        <template #right>
          <el-tooltip
            v-if="canDuplicate"
            content="Duplicar ET"
            placement="bottom"
          >
            <span class="mr-2">
              <el-button
                type="default"
                size="default"
                :disabled="!especificacionTecnica?.Codigo"
                @click="openDuplicateModal"
              >
                <icon-copy /> Duplicar ET
              </el-button>
            </span>
          </el-tooltip>

          <el-tooltip
            v-if="canCreateSolicitud"
            :content="getDocumentTooltip('Solicitud')"
            placement="bottom"
          >
            <span
              class="mr-2"
              :class="{ 'disabled-wrapper': !canGenerateDocuments }"
            >
              <el-button
                type="info"
                size="default"
                :disabled="!canGenerateDocuments"
                :style="!canGenerateDocuments ? 'pointer-events: none;' : ''"
                @click="openGenerateDocumentModal('SOLICITUD')"
              >
                <icon-plus /> Crear Solicitud
              </el-button>
            </span>
          </el-tooltip>

          <el-tooltip
            v-if="canCreateCotizacion"
            :content="getDocumentTooltip('Cotización')"
            placement="bottom"
          >
            <span
              class="mr-2"
              :class="{ 'disabled-wrapper': !canGenerateDocuments }"
            >
              <el-button
                type="warning"
                size="default"
                :disabled="!canGenerateDocuments"
                :style="!canGenerateDocuments ? 'pointer-events: none;' : ''"
                @click="openGenerateDocumentModal('COTIZACION')"
              >
                <icon-plus /> Crear Cotización
              </el-button>
            </span>
          </el-tooltip>

          <el-tooltip
            v-if="canCreateOP"
            :content="getDocumentTooltip('OP')"
            placement="bottom"
          >
            <span :class="{ 'disabled-wrapper': !canGenerateDocuments }">
              <el-button
                type="primary"
                size="default"
                :disabled="!canGenerateDocuments"
                :style="!canGenerateDocuments ? 'pointer-events: none;' : ''"
                @click="openGenerateDocumentModal('OP')"
              >
                <icon-plus /> Crear OP
              </el-button>
            </span>
          </el-tooltip>

          <modal-generate-op-from-et
            v-model="modalGenOpVisible"
            :codigoET="genOpCodigoET"
            :codigoElementoTipo="genOpCodigoElementoTipo"
            :initialCodigoUnidadVenta="especificacionTecnica.CodigoUnidadVenta"
            :tipoDocumento="documentTypeToGenerate"
            :isGlobal="especificacionTecnica.EsGlobal"
            @confirm="confirmGenerateDocument"
          />
          <modal-duplicate-technical-specification
            v-model="modalDuplicateVisible"
            :codigoET="duplicateCodigoET"
            @confirm="confirmDuplicate"
          />
        </template>
      </main-buttons-bar>

      <el-alert
        v-if="!canViewThisET"
        title="Esta especificación técnica no está asociada a su usuario. Solo puede consultarla."
        type="warning"
        show-icon
        :closable="false"
        style="margin-bottom: 20px"
      />
      <detail-container>
        <loading-overlay :show="isLoading" />

        <div
          class="detail-container"
          :class="{ 'et-readonly': !canEditET }"
        >
          <el-row :gutter="24">
            <custom-data
              :label="'Nro de ET'"
              :spanColumn="3"
              :data="especificacionTecnica?.CodigoBase || especificacionTecnica?.Codigo"
            ></custom-data>
            <el-col :span="3">
              <div class="custom-label">Versión</div>
              <div style="display: flex; align-items: center; gap: 10px">
                <span style="font-weight: 500; color: #111">
                  V{{ especificacionTecnica?.Version || 1 }}
                </span>
                <el-button
                  v-if="especificacionTecnica?.CodigoBase"
                  class="keep-active"
                  type="info"
                  link
                  size="small"
                  @click="modalHistorialVisible = true"
                >
                  (Ver historial)
                </el-button>
              </div>
            </el-col>
            <el-col :span="4">
              <div class="custom-label">Tipo de ET</div>
              <el-switch
                v-model="especificacionTecnica.EsGlobal"
                active-text="Global"
                inactive-text="Cliente"
                inline-prompt
                style="
                  --el-switch-on-color: #13ce66;
                  --el-switch-off-color: #409eff;
                  margin-top: 5px;
                "
                :disabled="!canEditET"
                @change="handleEsGlobalChange"
              />
            </el-col>
            <el-col :span="6">
              <div class="custom-label">Cliente/Prospecto (Principal)</div>
              <el-autocomplete
                v-model="especificacionTecnica.Cliente.Descripcion"
                size="default"
                :value-key="'Descripcion'"
                :class="{
                  'input-error':
                    !especificacionTecnica.EsGlobal &&
                    !validarEspecificacionTecnicaCampos.CodigoCliente,
                }"
                :fetch-suggestions="handleSearch"
                clearable
                :disabled="especificacionTecnica.EsGlobal || !canEditET"
                placeholder="Ingresar el cliente"
                style="width: 100%"
                @select="handleClientSelect"
                @clear="handleClientClear"
              />
            </el-col>
            <el-col :span="11">
              <input-additional-clients
                v-if="!especificacionTecnica.EsGlobal"
                v-model="especificacionTecnica.EspecificacionTecnicaClientes"
              />
            </el-col>
          </el-row>
          <el-row :gutter="24">
            <custom-select
              v-model="especificacionTecnica.Estado"
              :label="'Estado'"
              :spanColumn="4"
              :settings="{
                placeholder: 'Seleccionar estado',
                options: estadoSolicitudList,
                disabled: true,
              }"
              @update:modelValue="handleEstado"
            >
            </custom-select>
            <custom-data
              :label="'Fecha estado'"
              :spanColumn="3"
              :data="
                especificacionTecnica.FechaEstado
                  ? getDate('dd/mm/yyyy', especificacionTecnica.FechaEstado)
                  : ''
              "
            />
            <custom-data
              :label="'Usuario Creación'"
              :spanColumn="3"
              :data="`${especificacionTecnica.UsuarioCreacion?.Nombre || ''} ${
                especificacionTecnica.UsuarioCreacion?.Apellido || ''
              }`"
            />
            <custom-data
              :label="'Fecha creacion'"
              :spanColumn="2"
              :data="
                especificacionTecnica.FechaCreacion
                  ? getDate('dd/mm/yyyy', especificacionTecnica.FechaCreacion)
                  : ''
              "
            />
            <custom-data
              :label="'Usuario Modificación'"
              :spanColumn="3"
              :data="`${especificacionTecnica.UsuarioActualizacion?.Nombre || ''} ${
                especificacionTecnica.UsuarioActualizacion?.Apellido || ''
              }`"
            />
            <custom-data
              :label="'Fecha Modificación'"
              :spanColumn="3"
              :data="
                especificacionTecnica.FechaActualizacion
                  ? getDate('dd/mm/yyyy', especificacionTecnica.FechaActualizacion)
                  : ''
              "
            />
          </el-row>
          <el-row :gutter="24">
            <custom-input
              v-model="especificacionTecnica.Referencia"
              :label="'Descripción'"
              :spanColumn="10"
              :settings="{
                placeholder: 'Ingrese la descripción del producto',
                inputClass: { 'input-error': !validarEspecificacionTecnicaCampos.Referencia },
              }"
            />
            <custom-input-number
              v-model.number="especificacionTecnica.ToleranciaMinima"
              :label="'% Tolerancia Mínima'"
              :spanColumn="4"
              :settings="{
                placeholder: '% Tolerancia Mínima',
                min: 0,
                precision: 2,
                step: 0.01,
                controls: false,
              }"
            />
            <custom-input-number
              v-model.number="especificacionTecnica.ToleranciaMaxima"
              :label="'% Tolerancia Máxima'"
              :spanColumn="4"
              :settings="{
                placeholder: '% Tolerancia Máxima',
                min: 0,
                precision: 2,
                step: 0.01,
                controls: false,
              }"
            />
          </el-row>
          <el-row :gutter="24">
            <el-col :span="4">
              <div class="custom-label">Adjuntos</div>
              <el-upload
                class="upload-list"
                :file-list="fileList"
                :auto-upload="true"
                multiple
                :http-request="uploadAdjunto"
                accept=".jpg,.jpeg,.png,.webp,.pdf,.txt"
                :on-remove="handleRemove"
                :on-preview="handlePreview"
              >
                <el-button
                  class="button-container-upload"
                  type="primary"
                  size="default"
                  ><icon-upload></icon-upload> Subir adjuntos</el-button
                >
              </el-upload>
            </el-col>
            <custom-select
              v-model="especificacionTecnica.Usabilidad"
              :label="'Usabilidad'"
              :spanColumn="4"
              :settings="{
                placeholder: 'Seleccionar aplicación',
                options: usabilidadLista,
              }"
              @update:modelValue="handleUsabilidad"
            />
            <custom-select
              v-model="especificacionTecnica.UnidadVenta"
              :label="'Unidad venta'"
              :spanColumn="6"
              :settings="{
                placeholder: 'Seleccionar unidad',
                options: unidadesMedida,
              }"
              @update:modelValue="handleUnidadVenta"
            />
          </el-row>
          <InputProductosTerminados
            v-if="especificacionTecnica.EspecificacionTecnicaProducto"
            :productos="especificacionTecnica.EspecificacionTecnicaProducto"
            :codigoCliente="
              (especificacionTecnica.Cliente && especificacionTecnica.Cliente.Codigo) || null
            "
            :clientes="clientesAdicionales"
            :codigoElementoGrupo="elementoGrupo?.Codigo || null"
            @add-producto="technicalSpecificationStore.addProducto"
            @remove-producto="technicalSpecificationStore.removeProducto"
            @update-producto="technicalSpecificationStore.updateProducto"
          />

          <div
            v-for="(elemento, index) in modelo"
            :key="index"
          >
            <component
              :is="getComponenteDinamico(elemento.ElementoTipo.CodigoElementoBase)"
              v-if="configReady && elemento.ElementoTipo?.CodigoElementoBase"
              v-model="modelo[index]"
              :title="`${elemento.ElementoTipo.Descripcion} ${index + 1}`"
              :name="`${elemento.ElementoTipo.Descripcion}${index}`"
            />
          </div>

          <el-divider />
          <el-row :gutter="20">
            <custom-input
              v-model="especificacionTecnica.ObservacionesCotizacion"
              :label="'Observaciones de Cotización'"
              :spanColumn="8"
              :settings="{
                placeholder: 'Observaciones de Cotización',
                maxlength: 800,
                showWordLimit: true,
                disabled: true,
              }"
            />

            <custom-input
              v-model="especificacionTecnica.Observaciones"
              :label="'Observaciones para Producción (OT)'"
              :spanColumn="8"
              :settings="{
                placeholder: 'Observaciones para Producción (OT)',
                maxlength: 800,
                showWordLimit: true,
              }"
            />

            <custom-input
              v-model="especificacionTecnica.Notas"
              :label="'Notas Internas'"
              :spanColumn="8"
              :settings="{
                placeholder: 'Notas Internas',
                maxlength: 800,
                showWordLimit: true,
              }"
            />
          </el-row>
          <el-alert
            v-if="especificacionTecnica?.Codigo && especificacionTecnica?.Vigente === false"
            title="VERSIÓN OBSOLETA"
            type="error"
            description="Estás visualizando una versión antigua de esta Especificación Técnica. No es posible editarla ni aprobarla."
            show-icon
            :closable="false"
            style="margin-bottom: 20px"
          />
          <div
            v-if="canEditET"
            class="container"
          >
            <custom-confirm-buttons
              :disabled="
                (especificacionTecnica.Estado?.Descripcion !== 'Pendiente' &&
                  especificacionTecnica.Estado?.Descripcion !== 'A Cotizar') ||
                isObsolete
              "
              :confirmDisabled="
                especificacionTecnica.Estado?.Descripcion === 'Cotizada' || isObsolete
              "
              :showLoading="false"
              :confirmText="'Guardar'"
              :cancelText="'Cancelar'"
              :isSolicitud="true"
              @on-cancel="onCancel"
              @on-confirm="handleSave"
              @on-anular="onAnular"
            ></custom-confirm-buttons>
          </div>

          <div class="container footer-actions">
            <el-button
              v-if="especificacionTecnica.Codigo"
              type="primary"
              size="default"
              @click="handlePrint"
            >
              <icon-print /> Imprimir
            </el-button>

            <el-tooltip
              v-if="especificacionTecnica.Codigo && canEditET"
              :content="
                isObsolete
                  ? 'Esta ET corresponde a una versión obsoleta. No es posible aprobarla.'
                  : isApproved
                    ? 'La ET ya se encuentra aprobada'
                    : 'Aprobar ET'
              "
              placement="top"
            >
              <span :class="{ 'disabled-wrapper': isApproved || isObsolete }">
                <el-button
                  type="success"
                  :disabled="isApproved || isObsolete"
                  size="default"
                  :style="isApproved || isObsolete ? 'pointer-events: none;' : ''"
                  @click="handleApprove"
                >
                  <icon-check /> Aprobar
                </el-button>
              </span>
            </el-tooltip>
          </div>
        </div>
      </detail-container>
    </template>
  </permission-gate>
  <modal-historial-versiones
    v-if="especificacionTecnica?.CodigoBase"
    v-model="modalHistorialVisible"
    :codigoBase="especificacionTecnica.CodigoBase"
    :codigoActual="especificacionTecnica.Codigo"
  />
</template>

<script setup>
import {
  ref,
  onMounted,
  onBeforeMount,
  computed,
  provide,
  onUnmounted,
  watch,
} from 'vue';
import {
  useRoute,
  useRouter,
} from 'vue-router';
import { storeToRefs } from 'pinia';
import {
  ElNotification,
  ElMessageBox,
} from 'element-plus';
import { useTechnicalSpecificationStore } from '@/views/technical_specifications/stores/technical_specification_store.js';
import { usePricesStore } from '@/views/prices/stores/prices_store';
import { useProspectStore } from '@/views/prospects/stores/prospect_store.js';
import { useRecursoStore } from '@/stores/recurso_store.js';
import { useRequestStore } from '@/views/requests/stores/request_store';
import { useEstadoSolicitudStore } from '@/stores/estado-solicitud.js';
import { useUsabilidadStore } from '@/stores/usabilidad/usabilidad_store.js';
import { useUnidadMedidaStore } from '@/stores/unidad_medida/unidad_medida_store.js';
import { useMateriaPrimaFamiliaStore } from '@/stores/materia_prima_familia/materia_prima_familia_store';
import { useConfiguracionStore } from '@/stores/configuracion-general.js';
import { useProcesoStore } from '@/stores/proceso_store';
import { useWorkOrderStore } from '@/views/work_orders/stores/work_order_store.js';
import { Modules } from '@/enum/modules';
import { useElementoGrupoStore } from '@/stores/elemento_grupo/elemento_grupo_store';
import { ElementoBase } from '@/enum/elemento_base';

import MainBreadcrumb from '@/shared/layout/main/main_breadcrum.vue';
import MainButtonsBar from '@/shared/layout/main/buttons_bar/main_buttons_bar.vue';
import ModalGenerateOpFromEt from '@/shared/components/modal_generate_op_from_et.vue';
import modalHistorialVersiones from '@/shared/components/modal_historial_versiones.vue';
import DetailContainer from '@/shared/layout/detail/detail_container.vue';
import LoadingOverlay from '@/shared/LoadingOverlay.vue';
import CustomData from '@/shared/components/custom_data.vue';
import CustomInput from '@/shared/components/custom_input.vue';
import CustomInputNumber from '@/shared/components/custom_input_number.vue';
import CustomConfirmButtons from '@/shared/components/custom_confirm_buttons.vue';
import CustomSelect from '@/shared/components/custom_select.vue';
import CustomTitle from '@/shared/components/custom_title.vue';
import IconUpload from '~icons/mdi/cloud-upload';
import IconPrint from '~icons/mdi/printer-outline';
import IconCheck from '~icons/mdi/check-bold';
import IconPlus from '~icons/mdi/plus';
import getDate from '@/shared/components/utils.js';
import { useMessageError } from '@/shared/composables/message_error.js';
import { useMessageSuccess } from '@/shared/composables/message_success.js';
import { useLoading } from '@/shared/composables/loading.js';
import inputAdditionalClients from '@/shared/components/input_additional_clients.vue';
import InputProductosTerminados from '@/shared/components/input_productos_terminados.vue';
import technical_specifications_elemento_model from '@/views/technical_specifications/models/technical_specification_elemento_model.js';
import { useElementos } from '@/shared/composables/useElementos.js';
import { useModulo } from '@/shared/composables/useModulo.js';
import { usePrint } from '@/shared/composables/print';
import { useElementoBaseStore } from '@/stores/elemento_base/elemento_base_store';
import { EstadoSolicitud } from '@/enum/estado_solicitud';
import PermissionGate from '@/shared/components/permission_gate.vue';
import ModalDuplicateTechnicalSpecification from '@/shared/components/modal_duplicate_technical_specification.vue';
import { useAuthStore } from '@/views/auth/auth_store';
import {
  canCreateModule,
  canEditExplicit,
} from '@/shared/permissions/permission.utils.js';
import { Role } from '@/enum/roles_enum.js';
import { conAnchoMaterialExtrusion } from '@/shared/utils/extrusion_ancho_material.js';
import { describirCamposInvalidos } from '@/shared/utils/validacion_campos.js';
import { provideErrorRegistry } from '@/shared/composables/use_error_registry.js';
import { notificarErroresValidacion } from '@/shared/utils/notificar_errores_validacion.js';

provide('currentModule', Modules.ESPECIFICACION_TECNICA);
const elementoBaseEtiquetas = computed(
  () => elementoBaseStore.elementoBaseConfig?.etiquetas ?? null,
);
provide('elementoBaseEtiquetas', elementoBaseEtiquetas);

const { tieneErrores, mensajes: mensajesErrores } = provideErrorRegistry();

const configuracionStore = useConfiguracionStore();
const technicalSpecificationStore = useTechnicalSpecificationStore();
const prospectStore = useProspectStore();
const recursoStore = useRecursoStore();
const estadoSolicitudStore = useEstadoSolicitudStore();
const usabilidadStore = useUsabilidadStore();
const unidadMedidaStore = useUnidadMedidaStore();
const materiaPrimaFamiliaStore = useMateriaPrimaFamiliaStore();
const procesoStore = useProcesoStore();
const elementoBaseStore = useElementoBaseStore();
const workOrderStore = useWorkOrderStore();
const priceStore = usePricesStore();
const requestStore = useRequestStore();

const elementoGrupoStore = useElementoGrupoStore();
const { elementoGrupo } = storeToRefs(elementoGrupoStore);

const { uploadPublicFileHTTP, validarClave } = configuracionStore;
const { firstEstadoSolicitud } = estadoSolicitudStore;
const { estadoSolicitudList } = storeToRefs(estadoSolicitudStore);
const { createFromETHTTP: createCotizacionFromET } = priceStore;
const { createFromETHTTP: createSolicitudFromET } = requestStore;

const { especificacionTecnica, validarEspecificacionTecnicaCampos } = storeToRefs(
  technicalSpecificationStore,
);
// Ver work_order_edit.vue: el backend agrupa los materiales ya elegidos en todo el documento
// por categoría, e input_materiales.vue lo inyecta para poblar sus combos remotos con lo ya
// seleccionado sin depender de qué página de la búsqueda tenga cargada.
const materialesSeleccionadosPorCategoria = computed(
  () => especificacionTecnica.value?.MaterialesSeleccionadosPorCategoria ?? {},
);
provide('materialesSeleccionadosPorCategoria', materialesSeleccionadosPorCategoria);
const { usabilidadLista } = storeToRefs(usabilidadStore);
const { unidadesMedida } = storeToRefs(unidadMedidaStore);
const { rangoMaximoToleranciaEntrega, rangoMinimoToleranciaEntrega, sellado, solapa } =
  storeToRefs(configuracionStore);
const { createFromETHTTP } = workOrderStore;
const { searchProspectsHTTP } = prospectStore;
const authStore = useAuthStore();
const usuarioActual = authStore.usuario();

const {
  resetEspecificacionTecnica,
  resetValidaciones,
  updateEspecificacionTecnicaHTTP,
  createEspecificacionTecnicaHTTP,
  findEspecificacionTecnicaPorCodigoHTTP,
  validarCampos,
  cloneEspecificacionTecnicaHTTP,
} = technicalSpecificationStore;

const route = useRoute();
const router = useRouter();
const fileList = ref([]);
const configReady = ref(false);
const modalGenOpVisible = ref(false);
const genOpCodigoET = ref(null);
const genOpCodigoElementoTipo = ref(null);
const documentTypeToGenerate = ref(null);
const modalDuplicateVisible = ref(false);
const duplicateCodigoET = ref(null);
const API_BASE = import.meta.env.VITE_URL_BASE_API || '';
const PUBLIC_BASE = API_BASE.replace(/\/api\/?$/, '');
const aprobacionPorPasswordHabilitada = computed(
  () => configuracionStore.aprobacionPorPasswordHabilitada,
);

const ElementoTipoSeleccionado = ref({});
const modalHistorialVisible = ref(false);

const firstElementoTipoCodigo = computed(() => {
  const e0 = especificacionTecnica.value?.EspecificacionTecnicaElemento?.[0];
  return e0?.CodigoElementoTipo ?? e0?.ElementoTipo?.Codigo ?? null;
});

const openDuplicateModal = () => {
  if (!especificacionTecnica.value?.Codigo) return;
  duplicateCodigoET.value = especificacionTecnica.value.Codigo;
  modalDuplicateVisible.value = true;
};

const confirmDuplicate = async ({ codigoET }) => {
  if (!canDuplicate.value) return notificarSinPermiso();
  try {
    useLoading().openLoading();
    const etDuplicada = await cloneEspecificacionTecnicaHTTP(codigoET);

    if (!etDuplicada?.Codigo) throw new Error('No se devolvió el código de la nueva ET');

    modalDuplicateVisible.value = false;
    useMessageSuccess().open(`¡Especificación Técnica duplicada exitosamente!`);

    router.push(`/page/technical-specifications/list`);
  } catch (e) {
    console.error(e);
    useMessageError().open('Ha ocurrido un error al intentar duplicar la Especificación Técnica.');
  } finally {
    useLoading().closeLoading();
  }
};
const clientesAdicionales = computed(() => {
  const ids = new Set();
  if (especificacionTecnica.value.CodigoCliente) {
    ids.add(Number(especificacionTecnica.value.CodigoCliente));
  }
  if (Array.isArray(especificacionTecnica.value.EspecificacionTecnicaClientes)) {
    especificacionTecnica.value.EspecificacionTecnicaClientes.forEach((c) => {
      if (c.CodigoCliente) ids.add(Number(c.CodigoCliente));
    });
  }
  return Array.from(ids);
});

const { modelo, initializeModelo, standardToModuleArray, isLoading } = useElementos();
const { getComponenteDinamico } = useModulo(Modules.ESPECIFICACION_TECNICA);

const items = [
  { label: 'Inicio', to: '/page/technical-specifications/list', replace: false },
  { label: 'Edición', to: null, replace: false },
];

const isApproved = computed(() => {
  return especificacionTecnica.value?.CodigoEstado === EstadoSolicitud.ACEPTADA;
});

const isObsolete = computed(() => especificacionTecnica.value?.Vigente === false);

const esEjecutivoDeVentas = (usuarioActual?.roles ?? []).some(
  (rol) => rol.Codigo === Role.EJECUTIVO_DE_VENTAS,
);

// Un ejecutivo solo ve ETs globales o asociadas a él
const canViewThisET = computed(() => {
  const et = especificacionTecnica.value;
  if (!esEjecutivoDeVentas || !et?.Codigo || et.EsGlobal) return true;
  if (et.CodigoUsuarioCreacion === usuarioActual.Codigo) return true;
  const cliente = et.Cliente;
  if (!cliente || cliente.CodigoVendedorPrincipal === undefined) return true;
  const principal = cliente.CodigoVendedorPrincipal ?? null;
  const secundario = cliente.CodigoVendedorSecundario ?? null;
  if (principal === null && secundario === null) return true;
  return principal === usuarioActual.Codigo || secundario === usuarioActual.Codigo;
});

const canEditET = computed(() => {
  if (!canViewThisET.value) return false;
  return especificacionTecnica.value?.Codigo
    ? canEditExplicit(usuarioActual, 'especificacion-tecnica')
    : canCreateModule(usuarioActual, 'especificacion-tecnica');
});
const canDuplicate = computed(
  () => canViewThisET.value && canCreateModule(usuarioActual, 'especificacion-tecnica'),
);
const canCreateSolicitud = computed(
  () => canViewThisET.value && canCreateModule(usuarioActual, 'solicitud'),
);
const canCreateCotizacion = computed(
  () => canViewThisET.value && canCreateModule(usuarioActual, 'cotizacion'),
);
const canCreateOP = computed(
  () => canViewThisET.value && canCreateModule(usuarioActual, 'orden-produccion'),
);
const canCreateByType = (type) =>
  ({
    SOLICITUD: canCreateSolicitud,
    COTIZACION: canCreateCotizacion,
    OP: canCreateOP,
  })[type]?.value === true;

const notificarSinPermiso = () =>
  ElNotification({
    title: 'Permisos',
    message: 'No posee permisos para realizar esta acción.',
    type: 'warning',
  });

const canGenerateDocuments = computed(() => {
  return especificacionTecnica.value?.Codigo && isApproved.value && !isObsolete.value;
});

const getDocumentTooltip = (documentName) => {
  if (isObsolete.value) {
    return 'Esta ET corresponde a una versión obsoleta. Para generar nuevos documentos debe utilizar la versión vigente o crear una nueva versión.';
  }
  if (!isApproved.value) {
    return `La ET debe estar Aprobada para crear una ${documentName}.`;
  }
  return `Crear ${documentName}`;
};

const openGenerateDocumentModal = (type) => {
  if (!canCreateByType(type)) return notificarSinPermiso();
  if (!isApproved.value) return;
  if (!especificacionTecnica.value?.Codigo) return;

  genOpCodigoET.value = especificacionTecnica.value.Codigo;
  genOpCodigoElementoTipo.value = firstElementoTipoCodigo.value;
  documentTypeToGenerate.value = type;
  modalGenOpVisible.value = true;
};

const toRel = (u = '') => {
  if (!u) return '';
  const m = u.match(/\/assets\/(.+)$/i);
  return m ? m[1] : u.replace(/^\/+/, '');
};
const toPublicUrl = (u = '') => {
  if (!u) return '';
  if (/^https?:\/\//i.test(u)) return u;
  if (u.startsWith('/assets/')) return `${PUBLIC_BASE}${u}`;
  return `${PUBLIC_BASE}/assets/${u.replace(/^\/+/, '')}`;
};

const ensureAdjuntos = () => {
  if (!especificacionTecnica.value.EspecificacionTecnicaAdjunto) {
    especificacionTecnica.value.EspecificacionTecnicaAdjunto = [];
  }
  return especificacionTecnica.value.EspecificacionTecnicaAdjunto;
};
const uploadToken = ref((crypto?.randomUUID && crypto.randomUUID()) || String(Date.now()));
const getUploadSubdir = () => {
  const code = especificacionTecnica.value?.Codigo;
  return code ? `adjuntos/et` : `adjuntos/et/tmp/${uploadToken.value}`;
};

const uploadAdjunto = async ({ file, onSuccess, onError }) => {
  try {
    const subdir = getUploadSubdir();
    const payload = await uploadPublicFileHTTP({ subdir, file });
    const relPath = toRel(payload.url);

    const adj = ensureAdjuntos();
    adj.push({
      Codigo: null,
      CodigoEspecificacionTecnica: especificacionTecnica.value?.Codigo ?? null,
      ItemAdjunto: adj.length + 1,
      Url: relPath,
      MimeType: payload.mimeType ?? null,
    });

    fileList.value = [
      ...fileList.value,
      { name: file.name, url: toPublicUrl(relPath), status: 'success' },
    ];

    onSuccess && onSuccess(payload);
  } catch (err) {
    console.error('uploadAdjunto error:', err);
    onError && onError(err);
  }
};

const handleRemove = (file, currentFiles) => {
  fileList.value = currentFiles;
  const adj = ensureAdjuntos();
  const fname = (file?.name || '').toLowerCase();

  const idx = adj.findIndex((item) => {
    const rel = toRel(item?.Url || '');
    return rel.split('/').pop()?.toLowerCase() === fname;
  });
  if (idx !== -1) adj.splice(idx, 1);
};

const handlePreview = (file) => {
  const url = file?.url;

  if (!url) {
    console.warn('Adjunto sin URL para preview:', file);
    return;
  }
  window.open(url, '_blank', 'noopener,noreferrer');
};

const handleSearch = async (query, cb) => {
  let results = [];
  if (query && query.length >= 3 && query !== 'null') {
    results = await searchProspectsHTTP(query);
  }
  cb(results);
};

const confirmGenerateDocument = async ({
  codigoET,
  cantidad,
  codigoUnidadVenta,
  codigoClienteDestino,
}) => {
  if (!canCreateByType(documentTypeToGenerate.value)) return notificarSinPermiso();
  try {
    useLoading().openLoading();

    const payload = {
      Cantidad: cantidad,
      CodigoUnidadVenta: codigoUnidadVenta,
    };

    if (codigoClienteDestino) {
      payload.CodigoCliente = codigoClienteDestino;
    }
    if (documentTypeToGenerate.value === 'OP') {
      const op = await createFromETHTTP(codigoET, payload);
      if (!op?.Codigo) throw new Error('No se devolvió el código de OP');
      useMessageSuccess().open(`OP generada exitosamente!`);
      modalGenOpVisible.value = false;
      router.push(`/page/work-orders/edit/${op.Codigo}`);
    } else if (documentTypeToGenerate.value === 'COTIZACION') {
      const cotizacion = await createCotizacionFromET(codigoET, payload);
      if (!cotizacion?.Codigo) throw new Error('No se devolvió el código de Cotización');
      useMessageSuccess().open(`Cotización generada exitosamente!`);
      modalGenOpVisible.value = false;
      router.push(`/page/prices/edit/${cotizacion.Codigo}`);
    } else if (documentTypeToGenerate.value === 'SOLICITUD') {
      const solicitud = await createSolicitudFromET(codigoET, payload);
      if (!solicitud?.Codigo) throw new Error('No se devolvió el código de Solicitud');
      useMessageSuccess().open(`Solicitud generada exitosamente!`);
      modalGenOpVisible.value = false;
      router.push(`/page/requests/edit/${solicitud.Codigo}`);
    }
  } catch (e) {
    console.error(e);
    useMessageError().open(
      `Ha ocurrido un error al intentar generar la ${documentTypeToGenerate.value}.`,
    );
  } finally {
    useLoading().closeLoading();
  }
};

const CFG_MIN_DESC = 'Rango mínimo de tolerancia entrega';
const CFG_MAX_DESC = 'Rango máximo de tolerancia entrega';

const resolveTolerancias = (clienteMin, clienteMax) => {
  const min =
    clienteMin && Number(clienteMin) !== 0
      ? Number(clienteMin)
      : rangoMinimoToleranciaEntrega.value;
  const max =
    clienteMax && Number(clienteMax) !== 0
      ? Number(clienteMax)
      : rangoMaximoToleranciaEntrega.value;
  return { min, max };
};

const setToleranciasFrom = (clienteLikeObj = {}) => {
  const { min, max } = resolveTolerancias(
    clienteLikeObj.ToleranciaMinima,
    clienteLikeObj.ToleranciaMaxima,
  );
  if (min != null) especificacionTecnica.value.ToleranciaMinima = min;
  if (max != null) especificacionTecnica.value.ToleranciaMaxima = max;
};

const handleClientSelect = async (item) => {
  if (item && item.Codigo) {
    if (!especificacionTecnica.value.Cliente) {
      especificacionTecnica.value.Cliente = {};
    }
    especificacionTecnica.value.Cliente = item;
    especificacionTecnica.value.CodigoCliente = item.Codigo;
    setToleranciasFrom(item);
  }
};

const handleClientClear = () => {
  especificacionTecnica.value.Cliente = { Descripcion: '' };
  especificacionTecnica.value.CodigoCliente = null;
  especificacionTecnica.value.ToleranciaMinima = null;
  especificacionTecnica.value.ToleranciaMaxima = null;
};

const handleEsGlobalChange = (isGlobal) => {
  if (isGlobal) {
    handleClientClear();
    especificacionTecnica.value.EspecificacionTecnicaClientes = [];
  }
};

function handleEstado() {
  especificacionTecnica.value.FechaEstado = getDate('yyyy/mm/dd');
  especificacionTecnica.value.CodigoEstado = especificacionTecnica.value.Estado.Codigo;
}

function handleUsabilidad(item) {
  especificacionTecnica.value.CodigoUsabilidad = item.Codigo;
}

function handleUnidadVenta(item) {
  especificacionTecnica.value.CodigoUnidadVenta = item.Codigo;
}

const onCancel = () => {
  resetEspecificacionTecnica();
  router.go(-1);
};

const onAnular = async () => {
  if (!canEditET.value) return notificarSinPermiso();
  especificacionTecnica.value.Estado = {
    Codigo: 5,
    Descripcion: '',
  };
  await handleSave();
};

function handlePrint() {
  usePrint().printReport('especificacion_tecnica', {
    Codigo: especificacionTecnica.value.Codigo,
  });
}

async function handleApprove() {
  if (!canEditET.value) return notificarSinPermiso();
  if (!validarCampos()) {
    ElNotification({
      title: 'Revisar formulario',
      message: describirCamposInvalidos(
        technicalSpecificationStore.camposEspecificacionTecnicaInvalidos,
      ),
      type: 'error',
    });
    return;
  }
  if (!especificacionTecnica.value.Codigo) {
    ElNotification({
      title: 'Error',
      message: 'Primero debes guardar la ET antes de aceptarla.',
      type: 'warning',
    });
    return;
  }

  try {
    if (aprobacionPorPasswordHabilitada.value) {
      const { value: password } = await ElMessageBox.prompt(
        'Ingrese la contraseña de aprobación',
        'Aprobar Especificación Técnica',
        {
          inputType: 'password',
          inputPlaceholder: 'Contraseña',
          confirmButtonText: 'Aprobar',
          cancelButtonText: 'Cancelar',
          closeOnClickModal: false,
          closeOnPressEscape: false,
          roundButton: false,
        },
      );
      const resp = await validarClave({
        claveIngresada: password,
        claveInterna: 'clave_aprobacion',
      });
      if (!resp?.data?.valido) {
        ElNotification({
          title: 'Contraseña',
          message: 'La contraseña ingresada es incorrecta',
          type: 'error',
        });
        return;
      }
    } else {
      await ElMessageBox.confirm(
        '¿Confirmás la aprobación de la Especificación Técnica?',
        'Confirmar',
        { type: 'warning', confirmButtonText: 'Aprobar', cancelButtonText: 'Cancelar' },
      );
    }
  } catch (err) {
    const canceled = err === 'cancel' || err?.action === 'cancel' || err?.action === 'close';
    if (!canceled) {
      console.error(err);
      useMessageError().open('Ha ocurrido un error al intentar aprobar la especificación técnica.');
    }
    return;
  }
  let opened = false;
  try {
    useLoading().openLoading();
    opened = true;

    especificacionTecnica.value.CodigoEstado = 3;
    especificacionTecnica.value.Estado = { Codigo: 3, Descripcion: 'Aceptada' };
    especificacionTecnica.value.FechaEstado = new Date();

    await updateEspecificacionTecnicaHTTP();
    useMessageSuccess().open('La especificación técnica se aprobó exitosamente!');
    router.go(-1);
  } catch (err) {
    console.error(err);
    useMessageError().open('Ha ocurrido un error al intentar aprobar la especificación técnica.');
  } finally {
    if (opened) useLoading().closeLoading();
  }
}

const handleSave = async () => {
  if (!canEditET.value) return notificarSinPermiso();
  // COTI-507: Para extrusion, el ancho de material sale de las bobinas del proceso.
  modelo.value = conAnchoMaterialExtrusion(
    modelo.value,
    ElementoTipoSeleccionado.value.CodigoElementoBase,
  );

  const elementosMapeados = standardToModuleArray(modelo.value, Modules.ESPECIFICACION_TECNICA);

  elementosMapeados.forEach((elemento) => {
    if (Array.isArray(elemento.EspecificacionTecnicaElementoProceso)) {
      elemento.EspecificacionTecnicaElementoProceso =
        elemento.EspecificacionTecnicaElementoProceso.filter(
          (proceso) => !proceso.MarcadoParaEliminacion,
        );
    }
  });
  especificacionTecnica.value.EspecificacionTecnicaElemento = elementosMapeados;
  especificacionTecnica.value.EspecificacionTecnicaElemento = standardToModuleArray(
    modelo.value,
    Modules.ESPECIFICACION_TECNICA,
  );
  if (!especificacionTecnica.value.Codigo) {
    especificacionTecnica.value.CodigoUsuarioCreacion = usuarioActual.Codigo;
  }
  especificacionTecnica.value.CodigoUsuarioActualizacion = usuarioActual.Codigo;
  if (!validarCampos()) {
    ElNotification({
      title: 'Revisar formulario',
      message: describirCamposInvalidos(
        technicalSpecificationStore.camposEspecificacionTecnicaInvalidos,
      ),
      type: 'error',
    });
    return;
  }
  const tieneErrorAnchoMaterial = modelo.value.some(
    (elemento) => elemento.ErrorAnchoMaterial === true,
  );

  if (tieneErrorAnchoMaterial) {
    ElNotification({
      title: 'Validación de item',
      message:
        'El ancho del item es menor al ancho mínimo necesario para realizar el trabajo, por favor revise.',
      type: 'error',
    });
    return;
  }

  // COTI-633: registro de errores reportados por los componentes hijos (procesos,
  // materiales, pliego vs. máximo de máquina...) — reemplaza los chequeos puntuales
  // que antes vivían acá uno por uno.
  if (tieneErrores.value) {
    notificarErroresValidacion(mensajesErrores.value);
    return;
  }
  const codigoSaved = especificacionTecnica.value.Codigo;

  if (codigoSaved && isApproved.value) {
    try {
      await ElMessageBox.confirm(
        'Se detectaron cambios sobre una ET aprobada. ¿Desea generar una nueva versión? (La versión actual dejará de estar vigente)',
        'Control de Versiones',
        {
          distinguishCancelAndClose: true,
          confirmButtonText: 'Sí, generar nueva versión',
          cancelButtonText: 'No, sobrescribir',
          type: 'warning',
        },
      );
      especificacionTecnica.value.GenerarNuevaVersion = true;
    } catch (action) {
      if (action === 'cancel') {
        especificacionTecnica.value.GenerarNuevaVersion = false;
      } else {
        return;
      }
    }
  } else {
    especificacionTecnica.value.GenerarNuevaVersion = false;
  }
  try {
    useLoading().openLoading();
    let codigoSaved = especificacionTecnica.value.Codigo;
    if (!codigoSaved) {
      const response = await createEspecificacionTecnicaHTTP();
      codigoSaved = response?.data?.Codigo || response?.Codigo;
    } else {
      const response = await updateEspecificacionTecnicaHTTP();
      const nuevoCodigo = response?.data?.Codigo || response?.Codigo;
      if (especificacionTecnica.value.GenerarNuevaVersion && nuevoCodigo) {
        codigoSaved = nuevoCodigo;
      }
    }

    if (codigoSaved) {
      if (Number(route.params.id) !== codigoSaved) {
        await router.replace({ path: `/page/technical-specifications/edit/${codigoSaved}` });
        await findEspecificacionTecnicaPorCodigoHTTP(codigoSaved);
      } else {
        await findEspecificacionTecnicaPorCodigoHTTP(codigoSaved);
        await initializeModelo(
          especificacionTecnica.value.EspecificacionTecnicaElemento,
          Modules.ESPECIFICACION_TECNICA,
        );
        if (!route.params.id) {
          router.replace({ path: `/page/technical-specifications/edit/${codigoSaved}` });
        }
      }
    }

    useMessageSuccess().open(`Los datos se guardaron exitosamente!`);
  } catch (err) {
    useMessageError().open(`Ha ocurrido un error al intentar guardar los datos.`);
  } finally {
    useLoading().closeLoading();
  }
};

watch(
  () => route.params.id,
  async (newId) => {
    if (!newId || +newId === especificacionTecnica.value?.Codigo) return;

    try {
      useLoading().openLoading();
      configReady.value = false;

      await findEspecificacionTecnicaPorCodigoHTTP(newId);

      if (especificacionTecnica.value?.EspecificacionTecnicaElemento) {
        await initializeModelo(
          especificacionTecnica.value.EspecificacionTecnicaElemento,
          Modules.ESPECIFICACION_TECNICA,
        );
      }

      const adj = especificacionTecnica.value.EspecificacionTecnicaAdjunto || [];
      fileList.value = adj.map((a) => {
        const rel = toRel(a?.Url || '');
        return {
          name: rel.split('/').pop(),
          url: toPublicUrl(rel),
          status: 'success',
        };
      });

      configReady.value = true;
    } catch (error) {
      console.error('Error al cambiar de versión:', error);
      useMessageError().open('Error al cargar la versión seleccionada.');
    } finally {
      useLoading().closeLoading();
    }
  },
);

onMounted(() => {
  const id = Number(route.params?.id || 0);
  if (especificacionTecnica.value.Estado) {
    especificacionTecnica.value.CodigoEstado = especificacionTecnica.value.Estado.Codigo;
    if (!id) {
      especificacionTecnica.value.FechaEstado = getDate('yyyy/mm/dd');
    }
  }
});

onBeforeMount(async () => {
  configReady.value = false;
  await configuracionStore.findAllConfiguracionesHTTP({ page: 1 });
  await firstEstadoSolicitud();
  resetValidaciones();

  recursoStore.firstRecurso();
  const id = route.params.id;

  if (id && +id > 0) {
    await findEspecificacionTecnicaPorCodigoHTTP(id);
    ElementoTipoSeleccionado.value =
      especificacionTecnica.value?.EspecificacionTecnicaElemento[0].ElementoTipo;

    localStorage.setItem(
      'tipoProductoSeleccionado',
      JSON.stringify(especificacionTecnica.value.EspecificacionTecnicaElemento[0].ElementoTipo),
    );

    elementoGrupo.value =
      especificacionTecnica.value.EspecificacionTecnicaElemento[0].ElementoTipo.ElementoGrupoDetalle[0].ElementoGrupo;

    // Inicializar fileList después de cargar los datos
    const adj = especificacionTecnica.value.EspecificacionTecnicaAdjunto || [];
    console.log('Adjuntos cargados:', adj);
    fileList.value = adj.map((a) => {
      const rel = toRel(a?.Url || '');
      return {
        name: rel.split('/').pop(),
        url: toPublicUrl(rel),
        status: 'success',
      };
    });
  } else {
    console.log('Establecer datos iniciales para Especificacion tecnica', elementoGrupo.value);

    if (route.query && 'from-order-import' in route.query) {
      elementoGrupo.value = JSON.parse(localStorage.getItem('tipoProductoSeleccionado'));
    }

    ElementoTipoSeleccionado.value = elementoGrupo.value.ElementoGrupoDetalle[0].ElementoTipo;

    if (!elementoGrupo.value || !elementoGrupo.value.Codigo) {
      ElNotification({
        title: 'Error',
        message:
          'Debe seleccionar un grupo de elementos antes de crear una especificacion tecnica.',
        type: 'error',
      });
      router.push(items[0].to);
      return;
    }
    especificacionTecnica.value.EspecificacionTecnicaElemento =
      elementoGrupo.value.ElementoGrupoDetalle.map((egd) => {
        return {
          ...JSON.parse(JSON.stringify(technical_specifications_elemento_model)),
          ElementoTipo: egd.ElementoTipo,
        };
      });

    const selladoDefault = sellado.value;
    const solapaDefault = solapa.value;

    especificacionTecnica.value.EspecificacionTecnicaElemento.forEach((elemento, index) => {
      if (index === 0) {
        if (
          elemento.Sellado === 0 &&
          ElementoTipoSeleccionado.value.CodigoElementoBase === ElementoBase.BOLSAS_BOX
        ) {
          elemento.Sellado = selladoDefault;
        }
        if (
          elemento.Solapa === 0 &&
          [ElementoBase.BOLSAS_FUELLE, ElementoBase.BOLSAS_BOX].includes(
            ElementoTipoSeleccionado.value.CodigoElementoBase,
          )
        ) {
          elemento.Solapa = solapaDefault;
        }
      }
    });

    await technicalSpecificationStore.setDefaults(elementoGrupo.value.Codigo);
  }
  await elementoBaseStore.findElementoBaseConfigHTTP(
    ElementoTipoSeleccionado.value.CodigoElementoBase,
    Modules.ESPECIFICACION_TECNICA,
  );
  await initializeModelo(
    especificacionTecnica.value.EspecificacionTecnicaElemento,
    Modules.ESPECIFICACION_TECNICA,
  );
  configReady.value = true;

  materiaPrimaFamiliaStore.emptyList();
  materiaPrimaFamiliaStore.findAllfamiliaMaterialHTTP({
    page: 0,
    sortBy: 'Descripcion',
    sort: 'asc',
  });

  usabilidadStore.emptyList();
  usabilidadStore.findAllUsabilidadHTTP({
    page: 0,
    CodigoElementoTipo: ElementoTipoSeleccionado.value.Codigo,
  });

  unidadMedidaStore.emptyList();
  unidadMedidaStore.findAllUnidadMedidaHTTP({
    EsUnidadVenta: true,
    page: 0,
    Activo: true,
    CodigoElementoTipo: ElementoTipoSeleccionado.value.Codigo,
  });

  procesoStore.emptyList();
  procesoStore.findAllProcesosHTTP({
    elementoTipoId: ElementoTipoSeleccionado.value.Codigo,
    modulo: Modules.ESPECIFICACION_TECNICA,
  });
});

onUnmounted(() => {
  resetEspecificacionTecnica();
});
</script>

<style scoped>
.detail-container {
  background-color: white;
  border: 3px solid lightgray;
  padding: 20px;
}

.custom-label {
  font-weight: 700;
  color: #111;
}

.container {
  display: flex;
  justify-content: flex-end;
}

.input-wrapper {
  display: inline-block;
  margin-right: 10px;
}

.quantity-inputs {
  display: flex;
  flex-wrap: nowrap;
}

.container-quantity-inputs {
  display: flex;
  overflow-x: auto;
  width: 100%;
}

.el-button-quantity {
  flex-shrink: 0;
  margin: 0;
}
.footer-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 1rem;
}

.disabled-wrapper {
  display: inline-block;
  cursor: not-allowed;
}

/* Sin permiso de edición la ET es solo lectura (consultar e imprimir) */
.et-readonly {
  pointer-events: none;
}
.et-readonly .keep-active,
.et-readonly .footer-actions {
  pointer-events: auto;
}
</style>
